import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Theme,
  Typography
} from "@material-ui/core";
import { Quote } from "../../api/Quote/Quote";
import { colors, Loader } from "swyft-react-core";
import { toast } from "react-toastify";
import ClearIcon from "@material-ui/icons/Clear";
import { useEffect } from "react";
import { fetchLocalHeroesQuote, fetchQuote } from "../../store/QuoteSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/rootReducer";
import { sendLocalHeroesQuote, updateQuote } from "../../api/Quote/QuoteApi";
import { allowAdminEdits } from "../../app/utils";
import DateFnsUtils from "@date-io/date-fns";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { EQuoteStatus } from "../../api/Quote/EQuoteStatus";
import { UpdateQuoteForm } from "../../api/Quote/UpdateQuoteForm";
import { IErrors } from "../../api/Types";

const useStyles = makeStyles((theme: Theme) => ({
  margin: {
    margin: theme.spacing(1)
  },
  input: {
    width: "100%"
  },
  button: {
    margin: theme.spacing(1)
  },
  title: {
    "padding": theme.spacing(2),
    "marginLeft": theme.spacing(2),
    "display": "flex",
    '& [class*="Button-label svg"]': {
      marginRight: theme.spacing(2)
    },
    "& button": {
      "marginLeft": theme.spacing(2),
      "&:disabled svg": {
        fill: colors.lightGrey,
        stroke: colors.lightGrey
      }
    }
  },
  main: {
    padding: theme.spacing(2)
  },
  fields: {
    "& tr": {
      "& td": {
        "borderBottom": 0,
        "&:first-of-type": {
          width: 100
        }
      }
    }
  },
  content: {
    marginTop: theme.spacing(2),
    border: "1px solid black",
    overflowY: "scroll",
    height: 400
  },
  actions: {
    "display": "flex",
    "justifyContent": "flex-end",
    "marginTop": theme.spacing(2),
    "& button": {
      minWidth: "10em",
      marginLeft: theme.spacing(3)
    }
  },
  commentDialog: {
    minWidth: 600,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column"
  },
  confirmDeleteDialog: {
    minWidth: 600,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column"
  }
}));

interface Props {
  isOpen: boolean;
  onClose: () => void;
  quote: Quote;
}

interface State {
  saving: boolean;
  error: string | null;
  requestedInstallationDate: Date | null;
}

export const LocalHeroesModal: React.FC<Props> = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [state, setState] = useState<State>({
    saving: false,
    requestedInstallationDate: null,
    error: ""
  });

  const { quote } = props;
  const { localHeroesQuote } = useSelector((state: RootState) => state.quote);

  const handleSend = async () => {
    if (state.saving) {
      return;
    }

    setState({ ...state, saving: true });

    await sendLocalHeroesQuote(quote.id)
      .then(() => {
        dispatch(fetchQuote(quote.id));
      })
      .then((_) => {
        toast.success("Send not yet implemented - copy values for now");
      })
      .finally(() => {
        setState({ ...state, saving: false });
      });
  };

  const handleApprove = async () => {
    if (state.saving) {
      return;
    }

    setState({ ...state, saving: true });

    var quoteRequest = {
      id: quote?.id,
      requestedInstallationDate: state.requestedInstallationDate,
      status: EQuoteStatus.Approved
    } as UpdateQuoteForm;

    await updateQuote(quoteRequest)
      .then(() => {
        dispatch(fetchQuote(quote.id));
      })
      .then((_) => {
        toast.success("CRM Updated, Draft Invoice Created/Updated!");
      })
      .catch((err) => {
        console.log(err);
        const errors: IErrors = {};
        Object.keys(err.errors).forEach((key: string) => {
          const fieldName = key.charAt(0).toLowerCase() + key.substring(1);
          errors[fieldName] = err.errors[key][0];
        });
        throw errors;
      })
      .finally(() => {
        setState({ ...state, saving: false });
      });
  };

  useEffect(() => {
    dispatch(fetchLocalHeroesQuote(quote.id));
  }, [quote.id, dispatch]);

  if (state.saving || !localHeroesQuote) {
    return (
      <Dialog
        open={props.isOpen}
        onClose={props.onClose}
        aria-labelledby="template-selection"
        aria-describedby="template-select"
        fullWidth={true}
        maxWidth="xl"
      >
        <Loader />
      </Dialog>
    );
  }

  return (
    <React.Fragment>
      <Dialog
        open={props.isOpen}
        onClose={props.onClose}
        aria-labelledby="template-selection"
        aria-describedby="template-select"
        fullWidth={true}
        maxWidth="xl"
      >
        <Box className={classes.title}>
          <Box flexGrow={1} mt={"12px"}>
            <Typography variant="h2">Send Local Heroes Quote</Typography>
          </Box>
          <IconButton
            onClick={(_) => {
              props.onClose();
            }}
          >
            <ClearIcon />
          </IconButton>
        </Box>

        <Box display="flex">
          <Box flexGrow={1} className={classes.main}>
            <Table className={classes.fields} size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">To</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" component="div">
                      <TextField
                        label="Email Address"
                        variant="outlined"
                        size="small"
                        value={quote.customer?.email}
                        className={classes.input}
                        disabled={true}
                      />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">Labour</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" component="div">
                      <TextField
                        label="Labour"
                        variant="outlined"
                        size="small"
                        value={localHeroesQuote?.labourPrice}
                        className={classes.input}
                        disabled={true}
                      />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">Parts</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" component="div">
                      <TextField
                        label="Parts"
                        variant="outlined"
                        size="small"
                        value={localHeroesQuote?.materialsPrice}
                        className={classes.input}
                        disabled={true}
                      />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">Description</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" component="div">
                      <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={localHeroesQuote?.quoteDescription}
                        variant="outlined"
                        disabled={true}
                        multiline
                        minRows={8}
                      />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">Installation Date</Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                          disableToolbar
                          variant="inline"
                          format="dd/MM/yyyy"
                          margin="normal"
                          id="date-picker-inline"
                          label="Requestead Installation Date"
                          value={state.requestedInstallationDate}
                          onChange={(date: Date | null) => {
                            setState({ ...state, requestedInstallationDate: date });
                          }}
                          KeyboardButtonProps={{
                            "aria-label": "change date"
                          }}
                        />
                      </MuiPickersUtilsProvider>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Box className={classes.actions}>
              <Button
                variant="contained"
                color="secondary"
                onClick={(_) => handleSend()}
                className={classes.button}
                disabled={!allowAdminEdits(quote)}
              >
                Send
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={(_) => handleApprove()}
                className={classes.button}
                disabled={!allowAdminEdits(quote)}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={(_) => {
                  props.onClose();
                }}
                className={classes.button}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </React.Fragment>
  );
};
